// /admin/v1/attribution — 归因审计看板 API client
//
// 覆盖:
//   GET /admin/v1/attribution/events         事件列表(游标分页, 可过滤)
//   GET /admin/v1/attribution/summary        概览统计 (status/kind 计数 + ack 率 + 趋势)
//   GET /admin/v1/attribution/events/:id/logs  单事件回传请求/响应审计

import { get } from './client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AttributionEventStatus = 'pending' | 'sent' | 'acked' | 'failed' | 'skipped';

export type EventKind =
  | 'register'
  | 'key_action'
  | 'first_pay'
  | 'repeat_pay'
  | 'share'
  | 'view_content'
  | 'add_to_wishlist';

export interface AttributionEvent {
  id: number;
  event_id: string;
  user_id?: string;
  app_id: number;
  ad_account_id?: number;
  click_id?: string;
  event_kind: EventKind;
  payload: Record<string, unknown>;
  status: AttributionEventStatus;
  attempts: number;
  last_error?: string;
  sent_at?: string;
  acked_at?: string;
  next_retry_at?: string;
  created_at: string;
  updated_at: string;
}

export interface AdEventLog {
  id: number;
  attribution_event_id?: number;
  ad_account_id?: number;
  direction: 'outbound' | 'inbound';
  raw_request?: Record<string, unknown>;
  raw_response?: Record<string, unknown>;
  http_status?: number;
  duration_ms?: number;
  error_msg?: string;
  created_at: string;
}

export interface DaySummary {
  date: string; // YYYY-MM-DD
  total: number;
  acked: number;
  failed: number;
  skipped: number;
}

export interface AttributionSummary {
  status_counts: Record<string, number>;
  kind_counts: Record<string, number>;
  total_acked: number;
  total_failed: number;
  /** acked/(acked+failed)*100; 0 when no terminal events */
  ack_rate_pct: number;
  trend_days: DaySummary[];
}

export interface ListAttributionEventsQuery {
  status?: AttributionEventStatus;
  event_kind?: EventKind;
  app_id?: number;
  click_id?: string;
  date_from?: string; // YYYY-MM-DD or RFC3339
  date_to?: string;
  cursor?: string;
  limit?: number;
}

export interface ListAttributionEventsResult {
  data: AttributionEvent[] | null;
  next_cursor: string;
}

export interface EventDetailResult {
  event: AttributionEvent;
  logs: AdEventLog[];
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/** 列归因事件 (游标分页, 支持 status/event_kind/click_id/日期 过滤). */
export function listAttributionEvents(
  q: ListAttributionEventsQuery = {},
): Promise<ListAttributionEventsResult> {
  // get() unwraps the {code, data} envelope and returns data directly.
  // But /attribution/events returns a raw object (not the standard envelope)
  // so we use get<> and the client will return env.data — which is the object.
  return get<ListAttributionEventsResult>('/admin/v1/attribution/events', q);
}

/** 概览统计: status/kind 计数 + ack 率 + N 天趋势. */
export function getAttributionSummary(opts?: {
  app_id?: number;
  trend_days?: number;
}): Promise<AttributionSummary> {
  return get<AttributionSummary>('/admin/v1/attribution/summary', opts);
}

/** 单事件回传审计 (event + ad_event_logs). */
export function getAttributionEventDetail(id: number): Promise<EventDetailResult> {
  return get<EventDetailResult>(`/admin/v1/attribution/events/${id}/logs`);
}
